// Globals
import React, { ChangeEvent, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getProductsList, productsSelector } from '../../feature';

// Components
import { StyledFilterRow, StyledSelect } from './ProductsFiltersContainerStyles';
import { Checkbox, Input, Select } from '@/ui';

// Modules
import { useCategoriesAutocomplete, getCategoriesOptions } from '@/modules/Categories';
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { ProductsFiltersFields, TGetProductsQuery, getActiveOptions, getStoresOptions } from '../../helpers';
import { GetProductsListDTO } from '../../dtos';
import { useIdsManager } from '@/context';
import { useStoresSearch } from '../../hooks';

type IProductsFiltersContainer = {
  children?: ReactNode;
  className?: string;
};

const ProductsFiltersContainer = ({ children, className }: IProductsFiltersContainer) => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const { productsList, query } = useAppState(productsSelector);
  const currentIds = useMemo(() => productsList?.data.map(({ id }) => id), [productsList?.data]);
  const { ids, selectedAll, removeIds, addIds } = useIdsManager();
  const allCurrentSelected = currentIds?.length && currentIds?.every((id) => ids.includes(id));

  const { list: categoriesList, onSearch: onCategoriesSearch } = useCategoriesAutocomplete({});
  const { storeList, searchStores } = useStoresSearch();

  // The bulk-select checkbox column is hidden for view-only roles (see ProductsList columns),
  // so its filter-row cell is dropped too. The actions column itself stays for everyone.
  const { canManage } = usePermissions();
  const canManageProduct = canManage(Resource.PRODUCT);

  // Only the network request is debounced; the query is updated synchronously so the controlled filter
  // inputs stay responsive and reflect state restored from the URL (e.g. after returning via "Back").
  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        dispatch(getProductsList(new GetProductsListDTO(searchParams)));
      }, 500),
    [dispatch],
  );

  const applyFilter = (newQuery: TGetProductsQuery) => {
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  // HTML `name` is decoupled from the API query key so browsers don't treat filters as personal-name fields.
  const handleSearch = (field: ProductsFiltersFields) => (e: ChangeEvent<HTMLInputElement>) => {
    applyFilter({ ...query, [field]: e.target.value || undefined, page: 1 });
  };

  const handleBooleanFilter = (name: string) => (value: string) => {
    applyFilter({ ...query, [name]: value, page: 1 });
  };

  const onSelectChange = (value: unknown, key: ProductsFiltersFields) => {
    applyFilter({ ...query, [key]: value || undefined, page: 1 });
  };

  const onSelectAllCurrent = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      addIds(currentIds ?? []);
    } else {
      removeIds(currentIds ?? []);
    }
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        {canManageProduct && (
          <th className={'checkbox-column'}>
            <Checkbox checked={allCurrentSelected || selectedAll} onChange={onSelectAllCurrent} />
          </th>
        )}
        <th />
        <th>
          <Input
            name="product_name"
            autoComplete="off"
            value={query?.[ProductsFiltersFields.TITLE] || ''}
            onChange={handleSearch(ProductsFiltersFields.TITLE)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <StyledSelect
            allowClear
            showSearch
            value={query?.[ProductsFiltersFields.PARTNER_IDS]}
            options={getStoresOptions(storeList ?? [])}
            onSearch={searchStores}
            onChange={(v) => onSelectChange(v, ProductsFiltersFields.PARTNER_IDS)}
            isMultiselect
            maxTagCount={1}
          />
        </th>
        <th>
          <Input
            name="product_sku"
            autoComplete="off"
            value={query?.[ProductsFiltersFields.SKU] || ''}
            onChange={handleSearch(ProductsFiltersFields.SKU)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            name="partner_sku"
            autoComplete="off"
            value={query?.[ProductsFiltersFields.EXTERNAL_ID] || ''}
            onChange={handleSearch(ProductsFiltersFields.EXTERNAL_ID)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <StyledSelect
            allowClear
            showSearch
            value={query?.[ProductsFiltersFields.CATEGORY_IDS]}
            options={getCategoriesOptions(categoriesList ?? [])}
            onSearch={onCategoriesSearch}
            onChange={(v) => onSelectChange(v, ProductsFiltersFields.CATEGORY_IDS)}
            isMultiselect
            maxTagCount={1}
          />
        </th>
        <th>
          <Select
            options={getActiveOptions(t)}
            placeholder={t('products_list_status')}
            value={query?.[ProductsFiltersFields.ACTIVE] || undefined}
            onChange={handleBooleanFilter(ProductsFiltersFields.ACTIVE)}
            size="large"
            allowClear
          />
        </th>
        <th />
      </StyledFilterRow>
    </thead>
  );
};

export default ProductsFiltersContainer;
